"use client";

import Link from "next/link";
import { motion } from "framer-motion";
import { Header } from "@/components/layout/header";
import { Footer } from "@/components/layout/footer";

const ease = [0.25, 0.1, 0.25, 1] as const;

const steps = [
  {
    number: "01",
    title: "Разместите продукт",
    description:
      "Добавьте подробное описание, загрузите галерею, вставьте видео — оформите страницу так, чтобы сообщество сразу почувствовало, о чём ваш проект.",
  },
  {
    number: "02",
    title: "Сообщество голосует",
    description:
      "В течение недели пользователи знакомятся с новыми продуктами, задают вопросы и голосуют за те, которые их впечатлили.",
  },
  {
    number: "03",
    title: "Лучшие получают внимание",
    description:
      "Топ-3 продукта недели получают видимость на главной, обратную связь от сообщества и своих первых пользователей.",
  },
];

const differentiators = [
  {
    label: "Полноценная страница вместо карточки",
    text: "Подробное описание, видео, галерея скриншотов — формат, который передаёт характер, а не обрезает его",
  },
  {
    label: "Недельные циклы, а не 24 часа",
    text: "У стартапов больше времени собрать аудиторию и голоса — не нужно гнаться за одним днём",
  },
  {
    label: "Курируемая подборка",
    text: "10–15 продуктов в неделю — качество важнее количества",
  },
  {
    label: "Бесплатно для всех",
    text: "Мы существуем на поддержку сообщества, а не на деньги стартапов",
  },
];

export default function AboutPage() {
  return (
    <div className="min-h-screen flex flex-col bg-background">
      <Header />
      <main className="flex-1">
        <div className="container max-w-3xl mx-auto px-4 md:px-6 py-16 md:py-24">
          {/* Hero / Mission */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease }}
            className="mb-20 md:mb-28"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-[1.1] mb-8">
              Мы показываем стартапы такими, какими их создали
            </h1>
            <p className="text-lg md:text-xl text-foreground-secondary leading-relaxed max-w-2xl">
              Не иконка со строчкой текста. Скриншоты, видео, галереи,
              развёрнутая история — всё, чтобы передать суть и атмосферу
              так, как задумал автор.
            </p>
          </motion.div>

          {/* How it works */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease }}
            className="mb-20 md:mb-28"
          >
            <p className="text-xs uppercase tracking-widest text-foreground-tertiary mb-8">
              Как это работает
            </p>
            <div className="space-y-0">
              {steps.map((step, i) => (
                <motion.div
                  key={step.number}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: i * 0.1, ease }}
                  className="border-t border-border py-8 md:py-10"
                >
                  <div className="flex gap-6 md:gap-8">
                    <span className="text-3xl md:text-4xl font-bold text-foreground-tertiary/40 tabular-nums shrink-0 leading-none pt-1">
                      {step.number}
                    </span>
                    <div>
                      <h3 className="text-lg md:text-xl font-semibold text-foreground mb-2">
                        {step.title}
                      </h3>
                      <p className="text-sm md:text-base text-foreground-secondary leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
              <div className="border-t border-border" />
            </div>
          </motion.div>

          {/* What makes us different */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease }}
            className="mb-20 md:mb-28"
          >
            <p className="text-xs uppercase tracking-widest text-foreground-tertiary mb-8">
              Чем мы отличаемся
            </p>
            <div className="space-y-6">
              {differentiators.map((item) => (
                <div key={item.label}>
                  <p className="text-base md:text-lg text-foreground font-medium">
                    {item.label}
                  </p>
                  <p className="text-sm md:text-base text-foreground-secondary mt-1">
                    {item.text}
                  </p>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Founder */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease }}
            className="mb-20 md:mb-28"
          >
            <p className="text-xs uppercase tracking-widest text-foreground-tertiary mb-8">
              Кто за этим стоит
            </p>
            <div className="flex items-start gap-5">
              <div className="h-14 w-14 md:h-16 md:w-16 rounded-full bg-foreground/5 border border-border flex items-center justify-center shrink-0">
                <span className="text-lg md:text-xl font-semibold text-foreground-secondary">
                  МО
                </span>
              </div>
              <div>
                <p className="text-base md:text-lg font-semibold text-foreground">
                  Максим Овчинников
                </p>
                <p className="text-sm text-foreground-tertiary mb-3">
                  Основатель
                </p>
                <p className="text-sm md:text-base text-foreground-secondary leading-relaxed">
                  Меня всегда раздражало, что на существующих площадках каждый
                  стартап выглядит как все остальные. Ты вкладываешь месяцы в
                  продукт, продумываешь дизайн, а тебе дают карточку 300 на 200
                  пикселей. Я хотел сделать место, где продукт можно показать
                  таким, каким ты его видишь. Так появился Peek Product.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Support CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, ease }}
            className="mb-16"
          >
            <div className="rounded-xl border border-border bg-card p-6 md:p-8">
              <p className="text-lg md:text-xl font-semibold text-foreground mb-3">
                Поддержите проект
              </p>
              <p className="text-sm md:text-base text-foreground-secondary leading-relaxed mb-6">
                Peek Product — некоммерческий проект. Мы не берём деньги со
                стартапов и не продаём рекламу. Если вам нравится то, что мы
                делаем — вы можете помочь нам развиваться дальше.
              </p>
              <div className="flex flex-wrap gap-3">
                <Link
                  href="https://boosty.to/peekproduct"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border border-foreground bg-foreground text-background px-5 py-2.5 text-sm font-medium transition-colors hover:bg-foreground/90"
                >
                  Поддержать на Boosty
                </Link>
                <Link
                  href="https://t.me/peekproduct"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-5 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-card-hover"
                >
                  Telegram-канал
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
